import React, { type ReactElement } from 'react';
import { Icon, InlineMessage, InputField } from '@saviynt/design-system';
import cx from 'classnames';

import { FieldLabel } from '../../../../shared/components';
import {
  NUMERIC_VALUE_VALIDATION,
  testIds,
  VALIDATION_TYPES,
} from '../../../../shared/constants';
import { type NumberField } from '../../../../shared/types';
import { HELPTEXT_VALIDATION } from '../../../FormBuilder/data-layer/validation/validationRules';

import styles from './PreviewFields.module.css';

interface NumberPreviewProps {
  field: NumberField;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
}

/**
 * NumberInput component for rendering number fields in form preview
 */
function NumberInput({
  field,
  value,
  onChange,
  error,
  isRequired: isRequiredProp,
  isReadOnly: isReadOnlyProp,
}: NumberPreviewProps): ReactElement {
  const {
    fieldName,
    label,
    placeholder,
    isReadOnly,
    helperText,
    infoTooltipText,
    validations,
    defaultValue,
  } = field;

  // Use prop if provided, otherwise fallback to validation rules
  const isRequired =
    isRequiredProp ??
    validations?.some((rule) => rule.type === VALIDATION_TYPES.REQUIRED) ??
    false;

  // Read-only if EITHER static config OR conditional logic says so (matches sibling components)
  const isReadOnlyResult = isReadOnly || isReadOnlyProp || false;

  return (
    <InputField
      id={fieldName}
      name={fieldName}
      size='medium'
      placeholder={placeholder ?? ''}
      value={value ?? defaultValue?.value ?? ''}
      setValue={(v: string) => onChange?.(v)}
      inputmode='numeric'
      regexPattern={NUMERIC_VALUE_VALIDATION.REGEX}
      label={
        <FieldLabel
          label={label}
          htmlFor={fieldName}
          isReadOnly={isReadOnlyResult}
          infoTooltipText={infoTooltipText}
        />
      }
      isRequired={isRequired}
      isDisabled={isReadOnlyResult}
      tabIndex={0}
      isCritical={!!error}
      CriticalHelperText={
        <InlineMessage
          text={error}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      }
      HelperText={
        helperText ? (
          <InlineMessage
            text={helperText.slice(0, HELPTEXT_VALIDATION.MAX_LENGTH)}
            colorTheme='neutral'
            size='small'
          />
        ) : undefined
      }
      classNames={{
        inputContainer: cx(isReadOnlyResult && styles.value__isReadOnly),
      }}
      dataTestId={testIds.FF_PREVIEW_NUMBER_INPUT_FIELD}
    />
  );
}

export default NumberInput;
